import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  ArrowRight,
  CheckCircle2,
  Globe,
  Zap,
  Users,
  FileText,
  ExternalLink,
  Shield,
  Clock,
  Star,
  MapPin,
  Mail
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo component - Updated to circular version
const Logo = () => (
  <img 
    src="/mushonga_logo_circular.png"
    alt="Mushonga Systems"
    className="h-10 w-auto"
  />
);

const FreeReviewPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    businessName: '',
    name: '',
    email: '',
    phone: '',
    websiteUrl: '',
    industry: '',
    challenge: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/free-review`, formData);
      if (response.data.status === 'success') {
        navigate('/free-review-success');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const benefits = [
    {
      icon: <Globe size={24} />,
      title: 'Website Review',
      description: 'We analyse your website design, mobile responsiveness, speed and conversion opportunities.'
    },
    {
      icon: <Zap size={24} />,
      title: 'Business Automation Review',
      description: 'We identify repetitive processes that could be automated to save time and reduce manual work.'
    },
    {
      icon: <Users size={24} />,
      title: 'CRM & Lead Management Review',
      description: 'We assess how enquiries are captured, tracked and converted into customers.'
    },
    {
      icon: <FileText size={24} />,
      title: 'Action Plan',
      description: 'Receive practical recommendations tailored specifically to your business.'
    }
  ];

  const caseStudyFeatures = [
    'Online Booking Platform',
    'Volunteer Management',
    'Automated Notifications',
    'Admin Dashboard',
    'Live Production Deployment'
  ];

  const industries = [
    { value: '', label: 'Select your industry' },
    { value: 'trades', label: 'Trades' },
    { value: 'hospitality', label: 'Restaurant / Hospitality' },
    { value: 'professional', label: 'Professional Services' },
    { value: 'charity', label: 'Charity / Non-Profit' },
    { value: 'retail', label: 'Retail' },
    { value: 'other', label: 'Other' }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="free-review-page">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Free Website & Systems Review | Mushonga Systems</title>
        <meta 
          name="description" 
          content="Get a free review of your website, systems and customer journey. Discover opportunities to improve conversions, automate processes and grow your business." 
        />
        <meta property="og:title" content="Free Website & Systems Review | Mushonga Systems" />
        <meta property="og:description" content="Discover how your business can generate more enquiries, reduce admin and improve customer experience through better websites, automation and business systems." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://mushongasystems.com/free-review" />
      </Helmet>
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-40 glass">
        <div className="section-container">
          <nav className="flex items-center justify-between py-4">
            <Link to="/" className="flex items-center gap-3">
              <Logo />
              <span className="text-white font-bold text-lg hidden sm:block">
                Mushonga Systems
              </span>
            </Link>
            <Link to="/" className="text-gray-400 hover:text-white text-sm transition-colors">
              Back to Home
            </Link>
          </nav>
        </div>
      </header>

      <main className="pt-20">
        {/* Hero Section */}
        <section className="py-16 sm:py-24 relative overflow-hidden">
          {/* Background effects */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-1/3 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-emerald-500/10 rounded-full blur-[120px] animate-pulse"></div>
            <div className="absolute bottom-1/4 right-1/4 w-48 sm:w-80 h-48 sm:h-80 bg-emerald-500/5 rounded-full blur-[100px]"></div>
          </div>

          <div className="section-container relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-4 py-2 rounded-full mb-6">
                <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                <span className="text-sm font-semibold text-emerald-400">Free Business Review</span>
              </div>

              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tighter text-white mb-6 leading-tight" data-testid="hero-headline">
                Get A Free Website & Systems Review
              </h1>

              <p className="text-base sm:text-lg text-gray-400 leading-relaxed mb-8 max-w-2xl mx-auto">
                Discover how your business can generate more enquiries, reduce admin and improve customer experience through better websites, automation and business systems.
              </p>

              <a
                href="#review-form"
                className="btn-primary inline-flex text-base"
                data-testid="hero-cta"
              >
                Get My Free Review
                <ArrowRight size={20} />
              </a>

              {/* Trust badges */}
              <div className="flex flex-wrap justify-center gap-6 mt-10">
                {[
                  { icon: <CheckCircle2 size={16} />, text: 'Free Review' },
                  { icon: <Shield size={16} />, text: 'No Obligation' },
                  { icon: <Clock size={16} />, text: 'Delivered Within 48 Hours' }
                ].map((badge, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-500 text-sm">
                    <span className="text-emerald-400">{badge.icon}</span>
                    <span>{badge.text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* What You'll Receive Section */}
        <section className="py-16 sm:py-24 bg-[#111827]/50">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                What's Included
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                What You'll Receive
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {benefits.map((benefit, i) => (
                <div 
                  key={i}
                  className="bg-[#1F2937] border border-white/10 rounded-xl p-6 card-glow"
                  data-testid={`benefit-${i}`}
                >
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
                    <div className="text-emerald-400">{benefit.icon}</div>
                  </div>
                  <h3 className="text-white font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Case Study Section */}
        <section className="py-16 sm:py-24">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                Proven Results
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                Real Client Deployment
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-start">
              {/* Left - Screenshots Gallery */}
              <div className="space-y-4">
                {/* Main Screenshot - Homepage */}
                <div className="bg-[#111827] border border-white/10 rounded-xl overflow-hidden">
                  <div className="flex items-center gap-2 px-4 py-2 border-b border-white/10">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-500"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-green-500"></div>
                    <span className="ml-2 text-xs text-gray-500">peterboroughrepaircafe.uk</span>
                  </div>
                  <img
                    src="https://customer-assets.emergentagent.com/job_process-boost-2/artifacts/806hwo39_Repair%20Cafe%20Homepage.png"
                    alt="Peterborough Repair Café Homepage"
                    className="w-full h-auto"
                  />
                </div>
                
                {/* Secondary Screenshots */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[#111827] border border-white/10 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-white/10">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="ml-1.5 text-[10px] text-gray-500">Admin Dashboard</span>
                    </div>
                    <img
                      src="https://customer-assets.emergentagent.com/job_process-boost-2/artifacts/blidx0q1_Admin%20dashboard.png"
                      alt="Admin Dashboard"
                      className="w-full h-auto"
                    />
                  </div>
                  <div className="bg-[#111827] border border-white/10 rounded-xl overflow-hidden">
                    <div className="flex items-center gap-1.5 px-3 py-1.5 border-b border-white/10">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                      <div className="w-2 h-2 rounded-full bg-green-500"></div>
                      <span className="ml-1.5 text-[10px] text-gray-500">Automation Workflow</span>
                    </div>
                    <img
                      src="https://customer-assets.emergentagent.com/job_process-boost-2/artifacts/1p4idiuv_n8n%20workflow.png"
                      alt="n8n Automation Workflow"
                      className="w-full h-auto"
                    />
                  </div>
                </div>
              </div>

              {/* Right - Content */}
              <div className="lg:sticky lg:top-28">
                <span className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                  <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></span>
                  LIVE SYSTEM
                </span>
                
                <h3 className="text-xl sm:text-2xl font-bold text-white mb-4">
                  Peterborough Repair Café
                </h3>

                <p className="text-gray-400 mb-6 leading-relaxed text-lg font-medium">
                  We don't just build websites. We build systems that businesses actively use.
                </p>

                <div className="space-y-3 mb-8">
                  {caseStudyFeatures.map((feature, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <CheckCircle2 className="text-emerald-400 flex-shrink-0" size={18} />
                      <span className="text-gray-300 text-sm">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="flex flex-wrap gap-4">
                  <a
                    href="https://peterboroughrepaircafe.uk"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-semibold hover:bg-emerald-500/20 transition-colors text-sm px-4 py-2 rounded-lg"
                  >
                    View Live Site
                    <ExternalLink size={14} />
                  </a>
                  <Link
                    to="/case-study/repair-cafe"
                    className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors text-sm px-4 py-2"
                  >
                    Read Full Case Study
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Form Section */}
        <section id="review-form" className="py-16 sm:py-24 bg-[#111827]/50">
          <div className="section-container">
            <div className="text-center mb-12">
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-3 block">
                Get Started
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tighter text-white">
                Request Your Free Review
              </h2>
              <p className="text-gray-400 mt-4 max-w-xl mx-auto">
                Fill in your details below and we'll analyse your business within 48 hours.
              </p>
            </div>

            <div className="max-w-2xl mx-auto">
              <form 
                onSubmit={handleSubmit}
                className="bg-[#1F2937] border border-white/10 rounded-2xl p-6 sm:p-8"
                data-testid="review-form"
              >
                <div className="grid sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Business Name *
                    </label>
                    <input
                      type="text"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="Your business name"
                      data-testid="input-business-name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="Your name"
                      data-testid="input-name"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="input-field"
                      placeholder="you@business.com"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="07XXX XXXXXX"
                      data-testid="input-phone"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5 mb-5">
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Website URL
                    </label>
                    <input
                      type="url"
                      name="websiteUrl"
                      value={formData.websiteUrl}
                      onChange={handleChange}
                      className="input-field"
                      placeholder="https://yourwebsite.com"
                      data-testid="input-website"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      Industry *
                    </label>
                    <select
                      name="industry"
                      value={formData.industry}
                      onChange={handleChange}
                      required
                      className="input-field appearance-none cursor-pointer"
                      data-testid="input-industry"
                    >
                      {industries.map((ind) => (
                        <option key={ind.value} value={ind.value}>{ind.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mb-6">
                  <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    What is the biggest challenge in your business right now? *
                  </label>
                  <textarea
                    name="challenge"
                    value={formData.challenge}
                    onChange={handleChange}
                    required
                    className="textarea-field"
                    placeholder="Tell us about your current challenges, goals, or what you'd like to improve..."
                    rows={5}
                    data-testid="input-challenge"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary w-full justify-center text-base disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {isSubmitting ? 'Submitting...' : (
                    <>
                      Request My Free Review
                      <ArrowRight size={20} />
                    </>
                  )}
                </button>

                <p className="text-gray-600 text-xs text-center mt-4">
                  Your information is secure and will never be shared with third parties.
                </p>
              </form>
            </div>
          </div>
        </section>

        {/* Trust Section */}
        <section className="py-16 sm:py-20">
          <div className="section-container">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              {[
                { icon: <CheckCircle2 size={20} />, title: 'Real Client Deployment', desc: 'Live production systems' },
                { icon: <Star size={20} />, title: 'Google Business Reviews', desc: 'Verified feedback' },
                { icon: <MapPin size={20} />, title: 'Local UK Business', desc: 'Based in Peterborough' },
                { icon: <Mail size={20} />, title: 'Professional Domain', desc: '@mushongasystems.com' }
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <div className="text-emerald-400">{item.icon}</div>
                  </div>
                  <p className="text-white font-semibold text-sm mb-1">{item.title}</p>
                  <p className="text-gray-500 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Testimonials */}
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-400 mb-2 block">
                  Client Feedback
                </span>
                <h3 className="text-xl font-bold text-white">What Our Clients Say</h3>
              </div>
              
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  {
                    name: 'Tony Vroon',
                    role: 'CEO',
                    org: 'Peterborough Repair Café',
                    quote: 'Enthusiastic and full of energy. Adam really understood what we needed and had a complete appointment booking system going for us within a week or two.'
                  },
                  {
                    name: 'Andrew Chapman',
                    role: 'Director',
                    org: 'Peterborough Repair Café',
                    quote: 'Adam built a website and an automated system to manage our bookings. He was diligent and responsive to all of our questions. Very helpful and professional.'
                  }
                ].map((testimonial, i) => (
                  <div 
                    key={i}
                    className="bg-[#1F2937] border border-white/10 rounded-xl p-6"
                  >
                    <div className="flex items-center gap-1 mb-3">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star key={star} className="text-yellow-400 fill-yellow-400" size={14} />
                      ))}
                    </div>
                    <p className="text-gray-400 italic text-sm mb-4 leading-relaxed">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                        <span className="text-emerald-400 font-bold text-sm">
                          {testimonial.name.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <div>
                        <p className="text-white font-semibold text-sm">{testimonial.name}</p>
                        <p className="text-gray-500 text-xs">{testimonial.role}, {testimonial.org}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t border-white/10">
        <div className="section-container">
          <div className="flex flex-col items-center gap-4 md:flex-row md:justify-between">
            <div className="flex items-center gap-3">
              <Logo />
              <span className="text-white font-bold text-sm">Mushonga Systems</span>
            </div>
            <div className="text-center">
              <p className="text-gray-600 text-xs">
                © {new Date().getFullYear()} Mushonga Systems. All rights reserved.
              </p>
              <p className="text-gray-600 text-[10px] mt-1">
                Mushonga Systems is a trading name of ATM29 LTD. Registered in England and Wales. Company No. 13457210.
              </p>
            </div>
            <Link to="/" className="text-gray-500 hover:text-white text-xs transition-colors">
              Back to Home
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default FreeReviewPage;
